import React, { useEffect, useState, useRef, useCallback } from "react";
import axiosInstance from "../../utils/api";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";

// ── Defaults ──────────────────────────────────────────────────────────────
const DEFAULT_HEADER = {
  image: null,
  imagePath: null,
  height: 90,
  overlay: false,
  overlayColor: "#000000",
  overlayOpacity: 40,
  // custom (no-image) header settings
  custom: {
    bgColor: "#0f172a",
    bgColor2: "#1e3a5f",
    gradientDir: "to right",
    showLogo: true,
    logoSize: 44,
    showEventName: true,
    eventNameFontSize: 15,
    eventNameColor: "#ffffff",
    eventNameWeight: "bold",
    showTagline: false,
    taglineText: "Badge d'Accès Officiel",
    taglineFontSize: 9,
    taglineColor: "#94a3b8",
    layout: "logo-left",   // logo-left | logo-right | logo-top | logo-only | text-only
    padding: 16,
  },
};

const DEFAULT_LAYOUT = {
  logo: { visible: true, position: "top", align: "left", size: 48 },
  eventName: { visible: true, fontSize: 14, fontWeight: "extrabold", color: "#0f172a", align: "left" },
  tagline: { visible: true, text: "Badge d'Accès Officiel", fontSize: 9, color: "#64748b", opacity: 85 },
  name: { visible: true, fontSize: 14, fontWeight: "bold", color: "#ffffff" },
  email: { visible: true, fontSize: 10, color: "#ffffff" },
  cin: { visible: true, fontSize: 11, color: "#ffffff", prefix: "CIN. NO: " },
  qrCode: { visible: true, size: 90, border: true, borderColor: "#cbd5e1" },
  footer: { visible: true, text: "ACCESS ALL AREAS • 24 HOURS" },
};

const DEFAULT_TYPE_CONFIG = {
  builder: { label: "BUILDER", subLabel: "OFFICIAL BUILDER", bodyColor: "#111827", accentColor: "#1f2937", layout: null },
  full_access: { label: "STAFF", subLabel: "OFFICIAL STAFF", bodyColor: "#14532d", accentColor: "#166534", layout: null },
  contractor: { label: "CONTRACTOR", subLabel: "OFFICIAL CONTRACTOR", bodyColor: "#991b1b", accentColor: "#7f1d1d", layout: null },
};

const TYPE_LABELS = { builder: "Builder", full_access: "Staff", contractor: "Contractor" };
const TAB_COLORS = {
  global: "border-indigo-500 text-indigo-600 bg-white",
  builder: "border-slate-700  text-slate-800  bg-white",
  full_access: "border-green-600  text-green-700  bg-white",
  contractor: "border-red-600    text-red-700    bg-white",
};
const FONTS = [
  { label: "System Sans", value: "ui-sans-serif, system-ui, sans-serif" },
  { label: "Serif", value: "Georgia, serif" },
  { label: "Mono", value: "ui-monospace, monospace" },
];

const CUSTOM_LAYOUTS = [
  { value: "logo-left", label: "Logo + Name", desc: "Logo left, name right" },
  { value: "logo-right", label: "Name + Logo", desc: "Name left, logo right" },
  { value: "logo-top", label: "Stacked", desc: "Logo above name" },
  { value: "logo-only", label: "Logo only", desc: "Centered logo" },
  { value: "text-only", label: "Name only", desc: "Event name centered" },
];

const GRADIENT_DIRS = [
  { value: "to right", label: "→ Left to Right" },
  { value: "to bottom", label: "↓ Top to Bottom" },
  { value: "135deg", label: "↘ Diagonal" },
  { value: "to bottom right", label: "↘ Corner" },
];

// ── Helpers ───────────────────────────────────────────────────────────────
const merge = (target, source) => {
  if (!source) return target;
  const out = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
      out[key] = merge(target[key] || {}, source[key]);
    } else if (source[key] !== undefined) {
      // allow explicit null to clear a value (e.g. removing an uploaded image)
      out[key] = source[key];
    }
  }
  return out;
};

// ── Custom Header Renderer (no image) ────────────────────────────────────
const CustomHeaderView = ({ cfg, logoUrl, eventName, height }) => {
  const c = merge(DEFAULT_HEADER.custom, cfg || {});
  const bg = `linear-gradient(${c.gradientDir}, ${c.bgColor}, ${c.bgColor2})`;
  const pad = c.padding || 16;

  const LogoEl = logoUrl && c.showLogo
    ? <img src={logoUrl} alt="logo" style={{ height: c.logoSize || 44, objectFit: "contain", flexShrink: 0 }} />
    : null;

  const NameEl = c.showEventName
    ? (
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
        <div style={{
          fontSize: c.eventNameFontSize || 15,
          fontWeight: c.eventNameWeight === "bold" ? 700 : c.eventNameWeight === "extrabold" ? 800 : 600,
          color: c.eventNameColor || "#ffffff",
          lineHeight: 1.2,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {eventName || "Event Name"}
        </div>
        {c.showTagline && (
          <div style={{
            fontSize: c.taglineFontSize || 9,
            color: c.taglineColor || "#94a3b8",
            marginTop: 3,
            letterSpacing: 0.5,
          }}>
            {c.taglineText || "Badge d'Accès Officiel"}
          </div>
        )}
      </div>
    )
    : null;

  const renderLayout = () => {
    switch (c.layout) {
      case "logo-left":
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
            {LogoEl}
            {NameEl}
          </div>
        );
      case "logo-right":
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
            {NameEl}
            {LogoEl}
          </div>
        );
      case "logo-top":
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: `${pad / 2}px ${pad}px`, width: "100%", height: "100%" }}>
            {LogoEl}
            {NameEl && <div style={{ textAlign: "center" }}>{NameEl}</div>}
          </div>
        );
      case "logo-only":
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            {LogoEl || <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>No logo uploaded</span>}
          </div>
        );
      case "text-only":
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
            {NameEl && <div style={{ textAlign: "center" }}>{NameEl}</div>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{
      width: "100%", height: height || 90,
      background: bg,
      flexShrink: 0,
      display: "flex", alignItems: "center",
      overflow: "hidden",
    }}>
      {renderLayout()}
    </div>
  );
};

// ── Badge Preview ─────────────────────────────────────────────────────────
const BadgeLive = ({ typeConfig, globalLayout, logoUrl, eventName, headerConfig }) => {
  const layout = merge(globalLayout || DEFAULT_LAYOUT, typeConfig?.layout || {});
  const bodyColor = typeConfig?.bodyColor || "#111827";
  const accentColor = typeConfig?.accentColor || "#1f2937";
  const label = typeConfig?.label || "BADGE";
  const subLabel = typeConfig?.subLabel || "OFFICIAL";

  const hdr = merge(DEFAULT_HEADER, headerConfig || {});
  const headerHeight = hdr.height || 90;
  const hasImage = !!hdr.image;

  const renderHeader = () => {
    if (hasImage) {
      return (
        <div style={{ flexShrink: 0, lineHeight: 0, height: headerHeight, position: "relative", overflow: "hidden" }}>
          <img src={hdr.image} alt="header" style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }} />
          {hdr.overlay && (
            <div style={{
              position: "absolute", inset: 0,
              backgroundColor: hdr.overlayColor || "#000000",
              opacity: (hdr.overlayOpacity ?? 40) / 100,
              pointerEvents: "none",
            }} />
          )}
        </div>
      );
    }
    return (
      <CustomHeaderView
        cfg={hdr.custom}
        logoUrl={logoUrl}
        eventName={eventName}
        height={headerHeight}
      />
    );
  };

  return (
    <div style={{
      width: "100%", height: "100%", borderRadius: 14, overflow: "hidden",
      background: "#ffffff", display: "flex", flexDirection: "column",
      fontFamily: layout.font || "ui-sans-serif, system-ui, sans-serif",
    }}>
      {renderHeader()}

      <div style={{
        flex: 1, background: bodyColor,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "space-between",
        padding: "20px 18px 16px", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04, pointerEvents: "none",
          backgroundImage: "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",
          backgroundSize: "20px 20px",
        }} />

        <div style={{ textAlign: "center", zIndex: 1 }}>
          <div style={{ fontSize: 9, color: "#fff", opacity: 0.55, letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>{subLabel}</div>
          <div style={{ fontSize: label.length > 9 ? 26 : 34, fontWeight: 900, color: "#fff", letterSpacing: 0.5, lineHeight: 1.05, textTransform: "uppercase", textAlign: "center" }}>{label}</div>
        </div>

        <div style={{ textAlign: "center", zIndex: 1, width: "100%" }}>
          {layout.cin?.visible && (
            <div style={{ fontSize: layout.cin?.fontSize || 11, color: layout.cin?.color || "#ffffff", opacity: 0.8, marginBottom: 8, letterSpacing: 1, fontWeight: 600 }}>
              {layout.cin?.prefix || "CIN. NO: "}<span style={{ fontFamily: "monospace" }}>0001</span>
            </div>
          )}
          {layout.qrCode?.visible && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ background: "#fff", padding: 8, border: `3px solid ${accentColor}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                <QRCode value="preview-badge-id" size={layout.qrCode?.size || 90} />
              </div>
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", zIndex: 1 }}>
          {layout.name?.visible && (
            <div style={{ fontSize: layout.name?.fontSize || 14, fontWeight: layout.name?.fontWeight === "bold" ? 700 : 600, color: layout.name?.color || "#ffffff", marginBottom: 2 }}>Jean Dupont</div>
          )}
          {layout.email?.visible && (
            <div style={{ fontSize: layout.email?.fontSize || 10, color: layout.email?.color || "#ffffff", opacity: 0.65 }}>jean.dupont@example.com</div>
          )}
        </div>

        {layout.footer?.visible && (
          <div style={{ zIndex: 1, width: "100%", borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 10, textAlign: "center" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: 1.5, textTransform: "uppercase" }}>{layout.footer?.text || "ACCESS ALL AREAS"}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── UI Primitives ─────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <button onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${checked ? "bg-indigo-600" : "bg-slate-200"}`}>
    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
  </button>
);

const ColorPicker = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between gap-2">
    {label && <span className="text-[11px] text-slate-500 flex-1">{label}</span>}
    <div className="flex items-center gap-1.5">
      <div className="relative w-6 h-6 rounded border border-slate-200 overflow-hidden shadow-sm cursor-pointer" style={{ backgroundColor: value }}>
        <input type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
      </div>
      <input type="text" value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-20 text-[11px] font-mono border border-slate-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300" maxLength={9} />
    </div>
  </div>
);

const Slider = ({ label, value, onChange, min = 8, max = 48, step = 1 }) => (
  <div className="flex items-center gap-2">
    {label && <span className="text-[11px] text-slate-500 w-24 flex-shrink-0">{label}</span>}
    <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="flex-1 h-1 accent-indigo-600" />
    <span className="text-[11px] font-mono text-slate-600 w-8 text-right">{value}</span>
  </div>
);

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{title}</span>
        <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
};

const VisRow = ({ label, checked, onChange, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <Toggle checked={checked} onChange={onChange} />
    </div>
    {checked && children && <div className="pl-2 border-l-2 border-indigo-100 space-y-2">{children}</div>}
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div className="flex items-center justify-between gap-2">
    {label && <span className="text-[11px] text-slate-500">{label}</span>}
    <select value={value} onChange={e => onChange(e.target.value)}
      className="text-[11px] border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300 bg-white">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const TextInput = ({ label, value, onChange, placeholder }) => (
  <div className="space-y-1">
    {label && <span className="text-[11px] text-slate-500 block">{label}</span>}
    <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
  </div>
);

const LOGO_POSITIONS = [
  { value: "top", label: "Top", svg: <svg viewBox="0 0 40 28" className="w-8 h-5"><rect x="2" y="2" width="36" height="8" rx="2" fill="currentColor" opacity="0.25" /><rect x="12" y="2" width="16" height="6" rx="1" fill="currentColor" /><rect x="2" y="13" width="24" height="3" rx="1" fill="currentColor" opacity="0.4" /></svg> },
  { value: "bottom", label: "Bottom", svg: <svg viewBox="0 0 40 28" className="w-8 h-5"><rect x="2" y="2" width="24" height="3" rx="1" fill="currentColor" opacity="0.4" /><rect x="2" y="18" width="36" height="8" rx="2" fill="currentColor" opacity="0.25" /><rect x="12" y="19" width="16" height="6" rx="1" fill="currentColor" /></svg> },
  { value: "inline-left", label: "Left", svg: <svg viewBox="0 0 40 28" className="w-8 h-5"><rect x="2" y="6" width="10" height="16" rx="2" fill="currentColor" /><rect x="15" y="8" width="23" height="3" rx="1" fill="currentColor" opacity="0.4" /></svg> },
  { value: "inline-right", label: "Right", svg: <svg viewBox="0 0 40 28" className="w-8 h-5"><rect x="2" y="8" width="23" height="3" rx="1" fill="currentColor" opacity="0.4" /><rect x="28" y="6" width="10" height="16" rx="2" fill="currentColor" /></svg> },
];
const ALIGN_OPTIONS = [
  { value: "left", label: "Left", svg: <svg viewBox="0 0 24 12" className="w-6 h-3"><rect x="0" y="2" width="8" height="8" rx="1" fill="currentColor" /><rect x="0" y="0" width="24" height="1.5" rx="0.5" fill="currentColor" opacity="0.2" /></svg> },
  { value: "center", label: "Center", svg: <svg viewBox="0 0 24 12" className="w-6 h-3"><rect x="8" y="2" width="8" height="8" rx="1" fill="currentColor" /><rect x="0" y="0" width="24" height="1.5" rx="0.5" fill="currentColor" opacity="0.2" /></svg> },
  { value: "right", label: "Right", svg: <svg viewBox="0 0 24 12" className="w-6 h-3"><rect x="16" y="2" width="8" height="8" rx="1" fill="currentColor" /><rect x="0" y="0" width="24" height="1.5" rx="0.5" fill="currentColor" opacity="0.2" /></svg> },
];

const LogoPositionControl = ({ position, align, onPosition, onAlign }) => {
  const isInline = position === "inline-left" || position === "inline-right";
  return (
    <div className="space-y-3">
      <div>
        <span className="text-[11px] text-slate-500 block mb-1.5">Position</span>
        <div className="grid grid-cols-2 gap-1.5">
          {LOGO_POSITIONS.map(p => (
            <button key={p.value} onClick={() => onPosition(p.value)}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg border transition-all ${position === p.value ? "bg-indigo-50 border-indigo-400 text-indigo-700" : "border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50"}`}>
              {p.svg}<span className="text-[10px] font-medium">{p.label}</span>
            </button>
          ))}
        </div>
      </div>
      {!isInline && (
        <div>
          <span className="text-[11px] text-slate-500 block mb-1.5">Align</span>
          <div className="grid grid-cols-3 gap-1.5">
            {ALIGN_OPTIONS.map(a => (
              <button key={a.value} onClick={() => onAlign(a.value)}
                className={`flex flex-col items-center gap-1 py-2 rounded-lg border transition-all ${align === a.value ? "bg-indigo-50 border-indigo-400 text-indigo-700" : "border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50"}`}>
                {a.svg}<span className="text-[10px] font-medium">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Header Section ────────────────────────────────────────────────────────
const HeaderSection = ({ headerConfig, onHeaderChange, onCustomChange, eventId, logoUrl }) => {
  const [headerFile, setHeaderFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  // headerPreview: use a local blob URL while a new file is staged,
  // otherwise mirror headerConfig.image (including null when removed)
  const [stagedPreview, setStagedPreview] = useState(null);
  const headerPreview = stagedPreview || headerConfig?.image || null;

  const handleFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setHeaderFile(file);
    setStagedPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!headerFile) return toast.error("Select an image first");
    setUploading(true);
    const fd = new FormData(); fd.append("header", headerFile);
    try {
      const { data } = await axiosInstance.post(`/event/${eventId}/badge-header`, fd);
      onHeaderChange("image", data.header);
      setHeaderFile(null);
      setStagedPreview(null); // headerConfig.image will now drive the preview
      toast.success("Header uploaded");
    } catch { toast.error("Failed to upload header"); }
    finally { setUploading(false); }
  };

  const handleRemove = () => {
    setHeaderFile(null);
    setStagedPreview(null);   // drop any staged blob
    onHeaderChange("image", null); // null clears the saved URL → custom builder activates
    onHeaderChange("imagePath", null);   // ✅ add this
  };

  const hdr = merge(DEFAULT_HEADER, headerConfig || {});
  const c = merge(DEFAULT_HEADER.custom, hdr.custom || {});
  const hasImage = !!hdr.image;

  return (
    <Section title="Header">
      {/* ── Upload area ── */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] text-slate-500">Image</span>
          {hasImage && (
            <span className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-600 border border-green-200 rounded font-medium">
              Image active
            </span>
          )}
          {!hasImage && (
            <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-500 border border-indigo-200 rounded font-medium">
              Custom header active
            </span>
          )}
        </div>

        <div
          className="border-2 border-dashed border-slate-200 rounded-xl overflow-hidden cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {headerPreview ? (
            <div className="relative group">
              <img src={headerPreview} alt="header preview" className="w-full h-16 object-cover block" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-[11px] font-medium">Click to replace</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-1 py-4">
              <svg className="w-5 h-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-[11px] text-slate-400">Upload image header</span>
              <span className="text-[10px] text-slate-300">Custom builder is used when no image</span>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

        {(headerFile || headerPreview) && (
          <div className="flex gap-2 mt-2">
            {headerFile && (
              <button onClick={handleUpload} disabled={uploading}
                className="flex-1 py-1.5 text-xs bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium disabled:opacity-50 transition-colors">
                {uploading ? "Uploading..." : "Upload to Server"}
              </button>
            )}
            {headerPreview && !headerFile && (
              <button onClick={handleRemove}
                className="flex-1 py-1.5 text-xs border border-red-200 text-red-500 hover:bg-red-50 rounded-lg font-medium transition-colors">
                Remove Image
              </button>
            )}
            {headerFile && (
              <button onClick={() => { setHeaderFile(null); setStagedPreview(null); }}
                className="px-3 py-1.5 text-xs border border-slate-200 text-slate-500 hover:bg-slate-50 rounded-lg transition-colors">
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Height — always shown ── */}
      <Slider label="Height (px)" value={hdr.height || 90} min={40} max={180} onChange={v => onHeaderChange("height", v)} />

      {/* ── IMAGE controls (only when image uploaded) ── */}
      {hasImage && (
        <VisRow label="Color Overlay" checked={hdr.overlay ?? false} onChange={v => onHeaderChange("overlay", v)}>
          <ColorPicker label="Overlay color" value={hdr.overlayColor || "#000000"} onChange={v => onHeaderChange("overlayColor", v)} />
          <Slider label="Opacity %" value={hdr.overlayOpacity ?? 40} min={5} max={95} onChange={v => onHeaderChange("overlayOpacity", v)} />
        </VisRow>
      )}

      {/* ── CUSTOM HEADER BUILDER (only when no image) ── */}
      {!hasImage && (
        <div className="space-y-3 pt-1">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
            <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Custom Header Builder</span>
          </div>

          {/* Layout picker */}
          <div>
            <span className="text-[11px] text-slate-500 block mb-1.5">Layout</span>
            <div className="grid grid-cols-1 gap-1">
              {CUSTOM_LAYOUTS.map(opt => (
                <button key={opt.value} onClick={() => onCustomChange("layout", opt.value)}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all ${c.layout === opt.value ? "bg-indigo-50 border-indigo-400" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                  <span className={`text-[11px] font-medium ${c.layout === opt.value ? "text-indigo-700" : "text-slate-700"}`}>{opt.label}</span>
                  <span className="text-[10px] text-slate-400">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Background gradient */}
          <div className="space-y-2">
            <span className="text-[11px] text-slate-500 block">Background</span>
            <div className="flex gap-2">
              <div className="flex-1">
                <ColorPicker label="From" value={c.bgColor} onChange={v => onCustomChange("bgColor", v)} />
              </div>
              <div className="flex-1">
                <ColorPicker label="To" value={c.bgColor2} onChange={v => onCustomChange("bgColor2", v)} />
              </div>
            </div>
            <SelectField
              label="Direction"
              value={c.gradientDir || "to right"}
              onChange={v => onCustomChange("gradientDir", v)}
              options={GRADIENT_DIRS}
            />
          </div>

          {/* Logo settings */}
          {(c.layout === "logo-left" || c.layout === "logo-right" || c.layout === "logo-top" || c.layout === "logo-only") && (
            <VisRow label="Show Logo" checked={c.showLogo ?? true} onChange={v => onCustomChange("showLogo", v)}>
              {logoUrl
                ? <Slider label="Logo height" value={c.logoSize || 44} min={20} max={80} onChange={v => onCustomChange("logoSize", v)} />
                : <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">No logo uploaded yet — upload one in the Logo section below.</p>
              }
            </VisRow>
          )}

          {/* Event name settings */}
          {c.layout !== "logo-only" && (
            <VisRow label="Show Event Name" checked={c.showEventName ?? true} onChange={v => onCustomChange("showEventName", v)}>
              <Slider label="Font size" value={c.eventNameFontSize || 15} min={9} max={28} onChange={v => onCustomChange("eventNameFontSize", v)} />
              <ColorPicker label="Color" value={c.eventNameColor || "#ffffff"} onChange={v => onCustomChange("eventNameColor", v)} />
              <SelectField label="Weight" value={c.eventNameWeight || "bold"} onChange={v => onCustomChange("eventNameWeight", v)}
                options={[{ value: "semibold", label: "Semibold" }, { value: "bold", label: "Bold" }, { value: "extrabold", label: "Extra Bold" }]} />
            </VisRow>
          )}

          {/* Tagline */}
          {c.layout !== "logo-only" && (
            <VisRow label="Show Tagline" checked={c.showTagline ?? false} onChange={v => onCustomChange("showTagline", v)}>
              <TextInput value={c.taglineText} onChange={v => onCustomChange("taglineText", v)} placeholder="Badge d'Accès Officiel" />
              <Slider label="Font size" value={c.taglineFontSize || 9} min={7} max={14} onChange={v => onCustomChange("taglineFontSize", v)} />
              <ColorPicker label="Color" value={c.taglineColor || "#94a3b8"} onChange={v => onCustomChange("taglineColor", v)} />
            </VisRow>
          )}

          {/* Padding */}
          <Slider label="Padding" value={c.padding || 16} min={8} max={40} onChange={v => onCustomChange("padding", v)} />
        </div>
      )}
    </Section>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────
const BadgeCustomization = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState("global");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();
  const [globalLayout, setGlobalLayout] = useState({ ...DEFAULT_LAYOUT });
  const [typeConfigs, setTypeConfigs] = useState({ ...DEFAULT_TYPE_CONFIG });
  const [headerConfig, setHeaderConfig] = useState({ ...DEFAULT_HEADER });

  useEffect(() => { fetchEvent(); }, []);

  const fetchEvent = async () => {
    try {
      const { data } = await axiosInstance.get(`/event/${id}`);
      const ev = data.event;
      setEvent(ev);
      const saved = ev.badgeConfig;
      if (saved) {
        setLogoPreview(saved.logo || null);
        if (saved.header) setHeaderConfig(merge(DEFAULT_HEADER, saved.header));
        if (saved.globalLayout) setGlobalLayout(merge(DEFAULT_LAYOUT, saved.globalLayout));
        const builderSaved = saved.types?.builder || saved.types?.build_up || {};
        setTypeConfigs({
          builder: { ...DEFAULT_TYPE_CONFIG.builder, ...builderSaved },
          full_access: { ...DEFAULT_TYPE_CONFIG.full_access, ...(saved.types?.full_access || {}) },
          contractor: { ...DEFAULT_TYPE_CONFIG.contractor, ...(saved.types?.contractor || {}) },
        });
      }
    } catch { toast.error("Failed to load event"); }
  };

  const handleHeaderChange = useCallback((field, value) => {
    // explicit assignment preserves null (e.g. removing uploaded image)
    setHeaderConfig(prev => { const next = { ...prev }; next[field] = value; return next; });
  }, []);

  // Update a field inside headerConfig.custom
  const handleCustomChange = useCallback((field, value) => {
    setHeaderConfig(prev => ({
      ...prev,
      custom: { ...(prev.custom || DEFAULT_HEADER.custom), [field]: value },
    }));
  }, []);

  const setLayout = useCallback((section, field, value) => {
    if (activeTab === "global") {
      setGlobalLayout(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    } else {
      setTypeConfigs(prev => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          layout: {
            ...(prev[activeTab].layout || {}),
            [section]: {
              ...(prev[activeTab].layout?.[section] || globalLayout[section] || {}),
              [field]: value,
            },
          },
        },
      }));
    }
  }, [activeTab, globalLayout]);

  const getLayout = useCallback((section) => {
    if (activeTab === "global") return globalLayout[section] || {};
    return merge(globalLayout[section] || {}, typeConfigs[activeTab]?.layout?.[section] || {});
  }, [activeTab, globalLayout, typeConfigs]);

  const setTypeField = (field, value) =>
    setTypeConfigs(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], [field]: value } }));

  const handleLogoFile = (e) => { const f = e.target.files[0]; if (!f) return; setLogo(f); setLogoPreview(URL.createObjectURL(f)); };
  const handleLogoUpload = async () => {
    if (!logo) return toast.error("Select a logo first");
    setUploading(true);
    const fd = new FormData(); fd.append("logo", logo);
    try {
      const { data } = await axiosInstance.post(`/event/${id}/badge-logo`, fd);
      setLogoPreview(data.logo); setLogo(null); toast.success("Logo uploaded");
    } catch { toast.error("Failed to upload logo"); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/event/${id}/badge-config`, {
        globalLayout,
        header: {
          image: headerConfig.image,
          imagePath: headerConfig.imagePath,
          height: headerConfig.height,
          overlay: headerConfig.overlay,
          overlayColor: headerConfig.overlayColor,
          overlayOpacity: headerConfig.overlayOpacity,
          custom: headerConfig.custom,
        },
        types: {
          builder: typeConfigs.builder,
          build_up: typeConfigs.builder,
          show_days: typeConfigs.builder,
          teardown: typeConfigs.builder,
          full_access: typeConfigs.full_access,
          contractor: typeConfigs.contractor,
        },
      });
      toast.success("Badge config saved!");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const resetGlobal = () => {
    setGlobalLayout({ ...DEFAULT_LAYOUT });
    setHeaderConfig(prev => ({ ...DEFAULT_HEADER, image: prev.image, imagePath: prev.imagePath }));
  };
  const resetType = () => setTypeConfigs(prev => ({ ...prev, [activeTab]: { ...DEFAULT_TYPE_CONFIG[activeTab], layout: null } }));

  const previewConfig = activeTab === "global" ? typeConfigs.builder : typeConfigs[activeTab];
  const DESIGN_TYPES = ["builder", "full_access", "contractor"];

  return (
    <div className="flex flex-col h-screen bg-slate-100 overflow-hidden">

      {/* Top bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-slate-800">Badge Designer</h1>
          <p className="text-xs text-slate-400">{event?.name || "Loading..."}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetGlobal} className="px-3 py-1.5 text-xs text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Reset All</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ── */}
        <div className="w-72 bg-white border-r border-slate-200 flex flex-col overflow-hidden flex-shrink-0">

          <div className="flex border-b border-slate-200 bg-slate-50 flex-shrink-0">
            {[
              { key: "global", label: "Global" },
              { key: "builder", label: "Builder" },
              { key: "full_access", label: "Staff" },
              { key: "contractor", label: "Contractor" },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2.5 text-[11px] font-semibold transition-colors border-b-2 px-1 ${activeTab === tab.key ? TAB_COLORS[tab.key] : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">

            {activeTab !== "global" && (
              <div className="px-4 py-2.5 border-b bg-slate-50 border-slate-100">
                <p className="text-[10px] text-slate-600 font-medium">
                  Editing <strong>{TYPE_LABELS[activeTab]}</strong>
                  {activeTab === "builder" && <span className="text-slate-400 font-normal"> — applies to Build Up, Show Days &amp; Teardown</span>}
                </p>
                <button onClick={resetType} className="text-[10px] text-indigo-500 underline mt-0.5">Clear overrides</button>
              </div>
            )}

            {activeTab !== "global" && (
              <Section title="Badge Colors">
                <ColorPicker label="Body color" value={typeConfigs[activeTab]?.bodyColor} onChange={v => setTypeField("bodyColor", v)} />
                <ColorPicker label="Accent / QR border" value={typeConfigs[activeTab]?.accentColor} onChange={v => setTypeField("accentColor", v)} />
                <TextInput label="Main label" value={typeConfigs[activeTab]?.label} onChange={v => setTypeField("label", v)} placeholder="BUILDER" />
                <TextInput label="Sub-label" value={typeConfigs[activeTab]?.subLabel} onChange={v => setTypeField("subLabel", v)} placeholder="OFFICIAL BUILDER" />
              </Section>
            )}

            {/* Header — Global tab only */}
            {activeTab === "global" && (
              <HeaderSection
                headerConfig={headerConfig}
                onHeaderChange={handleHeaderChange}
                onCustomChange={handleCustomChange}
                eventId={id}
                logoUrl={logoPreview}
              />
            )}

            {/* Logo */}
            <Section title="Logo">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-3 flex flex-col items-center gap-2 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                onClick={() => fileInputRef.current?.click()}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" className="h-10 object-contain" />
                  : <span className="text-xs text-slate-400">Click to choose logo</span>}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
              {logo && (
                <button onClick={handleLogoUpload} disabled={uploading}
                  className="w-full py-1.5 text-xs bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium disabled:opacity-50">
                  {uploading ? "Uploading..." : "Upload to Server"}
                </button>
              )}
              <VisRow label="Show Logo" checked={getLayout("logo").visible ?? true} onChange={v => setLayout("logo", "visible", v)}>
                <LogoPositionControl
                  position={getLayout("logo").position || "top"}
                  align={getLayout("logo").align || "left"}
                  onPosition={v => setLayout("logo", "position", v)}
                  onAlign={v => setLayout("logo", "align", v)}
                />
                <Slider label="Height (px)" value={getLayout("logo").size || 44} min={16} max={80} onChange={v => setLayout("logo", "size", v)} />
              </VisRow>
            </Section>

            {/* Header Text */}
            <Section title="Header Text">
              <VisRow label="Tagline" checked={getLayout("tagline").visible ?? true} onChange={v => setLayout("tagline", "visible", v)}>
                <TextInput value={getLayout("tagline").text} onChange={v => setLayout("tagline", "text", v)} placeholder="Badge d'Accès..." />
                <Slider label="Font size" value={getLayout("tagline").fontSize || 9} min={7} max={14} onChange={v => setLayout("tagline", "fontSize", v)} />
                <ColorPicker label="Color" value={getLayout("tagline").color || "#64748b"} onChange={v => setLayout("tagline", "color", v)} />
                <Slider label="Opacity %" value={getLayout("tagline").opacity ?? 85} min={20} max={100} onChange={v => setLayout("tagline", "opacity", v)} />
              </VisRow>
              <VisRow label="Event Name" checked={getLayout("eventName").visible ?? true} onChange={v => setLayout("eventName", "visible", v)}>
                <Slider label="Font size" value={getLayout("eventName").fontSize || 14} min={10} max={24} onChange={v => setLayout("eventName", "fontSize", v)} />
                <ColorPicker label="Color" value={getLayout("eventName").color || "#0f172a"} onChange={v => setLayout("eventName", "color", v)} />
                <SelectField label="Weight" value={getLayout("eventName").fontWeight || "extrabold"} onChange={v => setLayout("eventName", "fontWeight", v)}
                  options={[{ value: "semibold", label: "Semibold" }, { value: "bold", label: "Bold" }, { value: "extrabold", label: "Extra Bold" }]} />
                <SelectField label="Align" value={getLayout("eventName").align || "left"} onChange={v => setLayout("eventName", "align", v)}
                  options={[{ value: "left", label: "Left" }, { value: "center", label: "Center" }, { value: "right", label: "Right" }]} />
              </VisRow>
            </Section>

            {/* Person Info */}
            <Section title="Person Info">
              <VisRow label="Name" checked={getLayout("name").visible ?? true} onChange={v => setLayout("name", "visible", v)}>
                <Slider label="Font size" value={getLayout("name").fontSize || 14} min={10} max={24} onChange={v => setLayout("name", "fontSize", v)} />
                <ColorPicker label="Color" value={getLayout("name").color || "#ffffff"} onChange={v => setLayout("name", "color", v)} />
              </VisRow>
              <VisRow label="Email" checked={getLayout("email").visible ?? true} onChange={v => setLayout("email", "visible", v)}>
                <Slider label="Font size" value={getLayout("email").fontSize || 10} min={8} max={14} onChange={v => setLayout("email", "fontSize", v)} />
                <ColorPicker label="Color" value={getLayout("email").color || "#ffffff"} onChange={v => setLayout("email", "color", v)} />
              </VisRow>
              <VisRow label="Serial No (CIN. NO)" checked={getLayout("cin").visible ?? true} onChange={v => setLayout("cin", "visible", v)}>
                <Slider label="Font size" value={getLayout("cin").fontSize || 11} min={8} max={16} onChange={v => setLayout("cin", "fontSize", v)} />
                <ColorPicker label="Color" value={getLayout("cin").color || "#ffffff"} onChange={v => setLayout("cin", "color", v)} />
                <TextInput label="Prefix text" value={getLayout("cin").prefix} onChange={v => setLayout("cin", "prefix", v)} placeholder="CIN. NO: " />
              </VisRow>
            </Section>

            {/* QR */}
            <Section title="QR Code">
              <VisRow label="Show QR Code" checked={getLayout("qrCode").visible ?? true} onChange={v => setLayout("qrCode", "visible", v)}>
                <Slider label="Size (px)" value={getLayout("qrCode").size || 90} min={60} max={160} onChange={v => setLayout("qrCode", "size", v)} />
              </VisRow>
            </Section>

            {/* Footer */}
            <Section title="Footer">
              <VisRow label="Show Footer" checked={getLayout("footer").visible ?? true} onChange={v => setLayout("footer", "visible", v)}>
                <TextInput value={getLayout("footer").text} onChange={v => setLayout("footer", "text", v)} placeholder="ACCESS ALL AREAS • 24 HOURS" />
              </VisRow>
            </Section>

            {/* Typography */}
            {activeTab === "global" && (
              <Section title="Typography">
                <SelectField label="Global Font" value={globalLayout.font || FONTS[0].value}
                  onChange={v => setGlobalLayout(prev => ({ ...prev, font: v }))} options={FONTS} />
              </Section>
            )}
          </div>
        </div>

        {/* ── Preview ── */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-8">
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-700">
              {activeTab === "global" ? "Global Preview" : `${TYPE_LABELS[activeTab]} Preview`}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Live · Changes reflect instantly</p>
          </div>

          <div className="flex justify-center mb-10">
            <div style={{ width: 340, height: 520, borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.22)" }}>
              <BadgeLive typeConfig={previewConfig} globalLayout={globalLayout} logoUrl={logoPreview} eventName={event?.name} headerConfig={headerConfig} />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-8">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-5">All Badge Types</p>
            <div className="flex gap-5 justify-center flex-wrap">
              {DESIGN_TYPES.map(type => (
                <button key={type} onClick={() => setActiveTab(type)}
                  className={`relative rounded-xl overflow-hidden flex-shrink-0 transition-all ${activeTab === type
                    ? type === "full_access" ? "ring-2 ring-green-600 ring-offset-2"
                      : type === "contractor" ? "ring-2 ring-red-600 ring-offset-2"
                        : "ring-2 ring-slate-800 ring-offset-2"
                    : "ring-1 ring-slate-200 hover:ring-2 hover:ring-slate-400"
                    }`} style={{ width: 170, height: 260 }}>
                  <div style={{ width: 340, height: 520, transform: "scale(0.5)", transformOrigin: "top left", pointerEvents: "none" }}>
                    <BadgeLive typeConfig={typeConfigs[type]} globalLayout={globalLayout} logoUrl={logoPreview} eventName={event?.name} headerConfig={headerConfig} />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent py-2.5 px-3">
                    <p className="text-white text-[11px] font-semibold">{TYPE_LABELS[type]}</p>
                    {type === "builder" && <p className="text-white/60 text-[9px]">Build Up · Show Days · Teardown</p>}
                  </div>
                  {activeTab === type && (
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${type === "full_access" ? "bg-green-600" : type === "contractor" ? "bg-red-600" : "bg-slate-800"}`}>
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeCustomization;